<template>
  <custom-modal
    :visible="visible"
    :close-button="true"
    :close-outside="!isFileDialogOpen"
    @close="handleClose"
  >
    <div class="flex flex-col">
      <h3 class="text-3xl font-bold leading-6 text-color-1 mb-6">
        {{ $t('Invoices | branding | title') }}
      </h3>

      <div class="flex flex-col gap-y-1 mb-5">
        <div
          class="text-base font-medium leading-[150%] text-color-1 overflow-hidden text-ellipsis"
        >
          {{ $t('Invoices | branding | addDetails') }}
        </div>
        <div class="text-sm font-medium leading-[150%] text-color-4">
          {{ $t('Invoices | branding | detailsDescription') }}
        </div>
      </div>

      <div class="flex flex-col gap-y-6 mb-6">
        <base-input
          v-model="formData.companyName"
          :label="$t('Invoices | branding | companyName')"
          :placeholder="$t('Invoices | branding | companyNamePlaceholder')"
          :danger="formData.companyName.length > 50"
          :danger-caption="
            formData.companyName.length > 50
              ? $t('Invoices | branding | maxLength', { max: 50 })
              : ''
          "
        />

        <base-input
          v-model="formData.registrationNumber"
          :label="$t('Invoices | branding | registrationNumber')"
          :placeholder="
            $t('Invoices | branding | registrationNumberPlaceholder')
          "
          :danger="formData.registrationNumber.length > 80"
          :danger-caption="
            formData.registrationNumber.length > 80
              ? $t('Invoices | branding | maxLength', { max: 80 })
              : ''
          "
        />

        <base-input
          v-model="formData.legalAddress"
          :label="$t('Invoices | branding | legalAddress')"
          :placeholder="$t('Invoices | branding | legalAddressPlaceholder')"
          :danger="formData.legalAddress.length > 90"
          :danger-caption="
            formData.legalAddress.length > 90
              ? $t('Invoices | branding | maxLength', { max: 90 })
              : ''
          "
        />
      </div>

      <div class="flex flex-col mb-6">
        <div
          class="text-base font-medium leading-[150%] text-color-1 overflow-hidden text-ellipsis mb-3"
        >
          {{ $t('Invoices | branding | logo') }}
        </div>

        <div class="flex items-starts gap-5 mb-3">
          <input
            ref="fileInput"
            type="file"
            accept="image/png"
            class="hidden"
            @change="handleFileChange"
          />

          <div
            v-if="!logoUrl"
            class="flex w-[124px] h-[124px] min-w-[56px] min-h-[56px] p-4 justify-center items-center rounded-[10px] border border-color-35 bg-bg-1 cursor-pointer"
            @click.stop="openFileDialog"
            @mousedown.stop
          >
            <base-circular-loading
              v-if="isUploading"
              :diameter="24"
              :stroke-width="2"
              color="#8F42FF"
            />
            <svg-icon
              v-else
              name="ic-download"
              class="!w-6 !h-6 text-color-4"
            />
          </div>

          <div
            v-else
            class="flex w-[124px] h-[124px] min-w-[56px] min-h-[56px] rounded-[10px] border border-color-35 bg-bg-1 overflow-hidden cursor-pointer"
            @click.stop="openFileDialog"
            @mousedown.stop
          >
            <img :src="logoUrl" alt="Logo" class="w-full h-full object-cover" />
          </div>

          <div class="flex flex-col gap-3 flex-1">
            <div class="flex gap-3">
              <div
                class="flex min-w-[36px] p-[10px] justify-center items-center flex-1 rounded-[10px] bg-bg-3 cursor-pointer hover:opacity-80 transition-opacity"
                @click.stop="openFileDialog"
                @mousedown.stop
              >
                <span class="text-sm font-medium text-color-1">
                  {{ $t('Invoices | branding | upload') }}
                </span>
              </div>

              <div
                class="flex min-w-[36px] p-[10px] justify-center items-center flex-1 rounded-[10px] bg-bg-3 transition-colors hover:opacity-80 transition-opacity"
                :class="{
                  'cursor-pointer hover:bg-bg-4': logoUrl,
                  'opacity-50 cursor-not-allowed': !logoUrl,
                }"
                @click="logoUrl && handleDeleteLogo()"
              >
                <span
                  class="text-sm font-medium transition-colors"
                  :class="logoUrl ? 'text-color-1' : 'text-color-4'"
                >
                  {{ $t('Invoices | branding | delete') }}
                </span>
              </div>
            </div>

            <div
              class="text-sm font-medium leading-[150%] text-color-4 max-w-[310px]"
            >
              {{ $t('Invoices | branding | fileRequirements') }}
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <base-button
          class="flex-1"
          :disabled="isSubmitDisabled"
          @onClick="handleBrand"
        >
          <base-circular-loading
            v-if="isLoading"
            :diameter="24"
            :stroke-width="2"
            color="#8F42FF"
          />
          <span v-else>{{ $t('Invoices | branding | submit') }}</span>
        </base-button>

        <base-button class="flex-1" type="shade" @onClick="handleClose">
          {{ $t('Invoices | branding | back') }}
        </base-button>
      </div>
    </div>
  </custom-modal>
</template>

<script setup>
import { reactive, ref, watch, inject, computed } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import BaseInput from '@emcd-shared/base-input'
import BaseButton from '@emcd-shared/base-button'
import CustomModal from './CustomModal.vue'
import SvgIcon from '@/shared/ui/SvgIcon/SvgIcon.vue'
import BaseCircularLoading from '@/shared/ui/BaseCircularLoading/ui/index.vue'
import invoicesService from '@/shared/services/invoices.service'
import { TOAST_DISPATCHERS } from '@/shared/constants'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
})

const emits = defineEmits(['close'])

const store = useStore()
const { t } = useI18n()

const fileInput = ref(null)
const formData = reactive({
  companyName: '',
  registrationNumber: '',
  legalAddress: '',
})

const logoUrl = ref(null)
const isUploading = ref(false)
const isLoading = ref(false)
const isFileDialogOpen = inject('isFileDialogOpen')

const MAX_FILE_SIZE = 2 * 1024 * 1024
const MAX_IMAGE_DIMENSION = 1024

const isSubmitDisabled = computed(() => {
  const hasInvalidLength =
    formData.companyName.length > 50 ||
    formData.registrationNumber.length > 80 ||
    formData.legalAddress.length > 90

  const hasEmptyFields =
    !formData.companyName.trim() ||
    !formData.registrationNumber.trim() ||
    !formData.legalAddress.trim()

  return isLoading.value || hasInvalidLength || hasEmptyFields
})

watch(
  () => props.visible,
  async (newValue) => {
    if (newValue) {
      await loadCustomization()
    }
  }
)

async function loadCustomization() {
  try {
    const response = await invoicesService.getInvoiceCustomization()
    const data = response.data

    formData.companyName = data.company_name || ''
    formData.registrationNumber = data.registration_number || ''
    formData.legalAddress = data.registered_address || ''
    logoUrl.value = data.logo_url || null
  } catch (error) {
    console.error('Failed to load customization:', error)
  }
}

async function loadLogoOnly() {
  try {
    const response = await invoicesService.getInvoiceCustomization()
    logoUrl.value = response.data.logo_url || null
  } catch (error) {
    console.error('Failed to load logo:', error)
  }
}

function openFileDialog() {
  if (!isUploading.value) {
    isFileDialogOpen.value = true
    fileInput.value?.click()

    setTimeout(() => {
      window.addEventListener('focus', handleWindowFocus, { once: true })
    }, 100)
  }
}

function handleWindowFocus() {
  setTimeout(() => {
    isFileDialogOpen.value = false
  }, 100)
}

async function validateFile(file) {
  if (file.type !== 'image/png') {
    return {
      isValid: false,
      error: 'format',
    }
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: 'size',
    }
  }

  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(img.src)
      if (img.width !== img.height) {
        resolve({
          isValid: false,
          error: 'square',
        })
      } else if (
        img.width > MAX_IMAGE_DIMENSION ||
        img.height > MAX_IMAGE_DIMENSION
      ) {
        resolve({
          isValid: false,
          error: 'resolution',
        })
      } else {
        resolve({ isValid: true })
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(img.src)
      resolve({
        isValid: false,
        error: 'format',
      })
    }
    img.src = URL.createObjectURL(file)
  })
}

function showValidationError(errorType) {
  const errorConfig = {
    format: {
      title: t('Invoices | branding | error | formatNotSupported | title'),
      message: t('Invoices | branding | error | formatNotSupported | message'),
    },
    size: {
      title: t('Invoices | branding | error | fileTooLarge | title'),
      message: t('Invoices | branding | error | fileTooLarge | message'),
    },
    square: {
      title: t('Invoices | branding | error | notSquare | title'),
      message: t('Invoices | branding | error | notSquare | message'),
    },
    resolution: {
      title: t('Invoices | branding | error | resolutionTooHigh | title'),
      message: t('Invoices | branding | error | resolutionTooHigh | message'),
    },
  }

  const config = errorConfig[errorType] || {
    title: t('Invoices | branding | error | uploadFailed | title'),
    message: t('Invoices | branding | error | uploadFailed | message'),
  }

  store.dispatch(
    TOAST_DISPATCHERS.setErrorMessage,
    {
      title: config.title,
      message: config.message,
    },
    { root: true }
  )
}

async function handleFileChange(event) {
  isFileDialogOpen.value = false
  const file = event.target.files?.[0]

  if (fileInput.value) {
    fileInput.value.value = ''
  }

  if (!file) return

  const validation = await validateFile(file)

  if (!validation.isValid) {
    showValidationError(validation.error)
    return
  }

  isUploading.value = true

  try {
    await invoicesService.uploadInvoiceLogo(file)

    await loadLogoOnly()

    store.dispatch(
      TOAST_DISPATCHERS.setSuccessMessage,
      {
        title: t('Invoices | branding | success | logoUploaded | title'),
        message: t('Invoices | branding | success | logoUploaded | message'),
      },
      { root: true }
    )
  } catch (error) {
    console.error('Logo upload error:', error)
    store.dispatch(
      TOAST_DISPATCHERS.setErrorMessage,
      {
        title: t('Invoices | branding | error | title'),
        message: t('Invoices | branding | error | message'),
      },
      { root: true }
    )
  } finally {
    isUploading.value = false
  }
}

async function handleDeleteLogo() {
  isUploading.value = true

  try {
    await invoicesService.deleteInvoiceLogo()

    await loadLogoOnly()

    store.dispatch(
      TOAST_DISPATCHERS.setSuccessMessage,
      {
        title: t('Invoices | branding | success | logoDeleted | message'),
        message: '',
      },
      { root: true }
    )
  } catch (error) {
    console.error('Logo delete error:', error)
    store.dispatch(
      TOAST_DISPATCHERS.setErrorMessage,
      {
        title: t('Invoices | branding | error | title'),
        message: t('Invoices | branding | error | message'),
      },
      { root: true }
    )
  } finally {
    isUploading.value = false
  }
}

async function handleBrand() {
  isLoading.value = true

  try {
    await invoicesService.upsertInvoiceCustomization({
      company_name: formData.companyName,
      registration_number: formData.registrationNumber,
      registered_address: formData.legalAddress,
      is_active: true,
    })

    store.dispatch(
      TOAST_DISPATCHERS.setSuccessMessage,
      {
        title: t('Invoices | branding | success | branded | title'),
        message: t('Invoices | branding | success | branded | message'),
      },
      { root: true }
    )

    handleClose()
  } catch (error) {
    console.error('Branding error:', error)
    store.dispatch(
      TOAST_DISPATCHERS.setErrorMessage,
      {
        title: t('Invoices | branding | error | title'),
        message: t('Invoices | branding | error | message'),
      },
      { root: true }
    )
  } finally {
    isLoading.value = false
  }
}

function handleClose() {
  emits('close')
}
</script>
